/**
 * 刷新控制组件
 * 提供手动刷新按钮和自动刷新开关
 */
import React from 'react';
import { Button, Switch, Space, Tooltip, Badge } from 'antd';
import { ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { AutoRefreshReturn } from '../../hooks/useAutoRefresh';

export interface RefreshControlProps {
  refreshState: AutoRefreshReturn;
  showAutoRefresh?: boolean;
  showLastRefreshTime?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
}

export const RefreshControl: React.FC<RefreshControlProps> = ({
  refreshState,
  showAutoRefresh = true,
  showLastRefreshTime = true,
  size = 'middle',
  style
}) => {
  const {
    isRefreshing,
    lastRefreshTime,
    nextRefreshIn,
    manualRefresh,
    toggleAutoRefresh,
    isAutoRefreshEnabled,
    cooldownRemaining = 0,
    canManualRefresh = true
  } = refreshState;

  const formatLastRefreshTime = (time: Date | null) => {
    if (!time) return '从未刷新';

    const now = new Date();
    const diff = Math.floor((now.getTime() - time.getTime()) / 1000);

    if (diff < 60) return `${diff}秒前`;
    if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
    return time.toLocaleString('zh-CN');
  };

  // 生成按钮文本
  const getButtonText = () => {
    if (isRefreshing) return '刷新中';
    if (cooldownRemaining > 0) return `刷新 (${cooldownRemaining}s)`;
    return '刷新';
  };

  // 生成提示文本
  const getTooltipText = () => {
    if (cooldownRemaining > 0) {
      return `请等待 ${cooldownRemaining} 秒后再刷新`;
    }
    if (showLastRefreshTime) {
      return `上次刷新: ${formatLastRefreshTime(lastRefreshTime)}`;
    }
    return '刷新数据';
  };

  return (
    <Space size="middle" style={style}>
      {/* 手动刷新按钮 */}
      <Tooltip title={getTooltipText()}>
        <Button
          type="default"
          size={size}
          icon={<ReloadOutlined spin={isRefreshing} />}
          onClick={manualRefresh}
          loading={isRefreshing}
          disabled={!canManualRefresh}
          style={cooldownRemaining > 0 ? { opacity: 0.7 } : undefined}
        >
          {getButtonText()}
        </Button>
      </Tooltip>

      {/* 自动刷新开关 */}
      {showAutoRefresh && (
        <Space size="small">
          <Switch
            checked={isAutoRefreshEnabled}
            onChange={toggleAutoRefresh}
            checkedChildren="自动"
            unCheckedChildren="手动"
            size={size === 'small' ? 'small' : 'default'}
          />
          {isAutoRefreshEnabled && nextRefreshIn > 0 && (
            <Tooltip title="距离下次自动刷新">
              <Badge count={nextRefreshIn} showZero style={{ backgroundColor: '#52c41a' }}>
                <ClockCircleOutlined style={{ fontSize: 16, color: '#52c41a' }} />
              </Badge>
            </Tooltip>
          )}
        </Space>
      )}
    </Space>
  );
};

/**
 * 数据变化提示组件
 */
export interface DataChangeIndicatorProps {
  hasChanges: boolean;
  changeCount?: number;
  onViewChanges?: () => void;
  style?: React.CSSProperties;
}

export const DataChangeIndicator: React.FC<DataChangeIndicatorProps> = ({
  hasChanges,
  changeCount,
  onViewChanges,
  style
}) => {
  if (!hasChanges) return null;

  return (
    <div
      style={{
        padding: '8px 16px',
        background: '#e6f7ff',
        border: '1px solid #91d5ff',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...style
      }}
    >
      <Space>
        <Badge status="processing" />
        <span style={{ color: '#1677ff' }}>
          {changeCount ? `检测到 ${changeCount} 条数据更新` : '数据已更新'}
        </span>
      </Space>
      {onViewChanges && (
        <Button type="link" size="small" onClick={onViewChanges}>
          查看更新
        </Button>
      )}
    </div>
  );
};

/**
 * 刷新状态指示器（小型）
 */
export interface RefreshIndicatorProps {
  isRefreshing: boolean;
  lastRefreshTime: Date | null;
  size?: 'small' | 'default';
}

export const RefreshIndicator: React.FC<RefreshIndicatorProps> = ({
  isRefreshing,
  lastRefreshTime,
  size = 'default'
}) => {
  const formatTime = (time: Date | null) => {
    if (!time) return '';
    return time.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Space size="small" style={{ color: '#8c8c8c', fontSize: size === 'small' ? 12 : 14 }}>
      <ReloadOutlined spin={isRefreshing} />
      <span>
        {isRefreshing ? '刷新中...' : lastRefreshTime ? `更新于 ${formatTime(lastRefreshTime)}` : '等待刷新'}
      </span>
    </Space>
  );
};
